import { Button, TextAreaInput, TextInput } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import React, { useMemo } from "react";
import { CXAutomationAccordion } from "../CXAutomationAccordion";
import { SectionDescription } from "../SectionDescription";
import { CXAutomationFormState } from "../../types";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

type Props = {
  value: Pick<CXAutomationFormState, "automationName" | "description">;
  onChange: (
    next: Partial<Pick<CXAutomationFormState, "automationName" | "description">>
  ) => void;
  isOpen: boolean;
  onOpenChange: (next: boolean) => void;
  onProceed: () => void;
};

export const CXAutomationGeneralSection: React.FC<Props> = ({
  value,
  onChange,
  isOpen,
  onOpenChange,
  onProceed,
}) => {
  const { t } = useTranslations();
  const { automationName, description } = value;

  const isSaveDisabled = useMemo(
    () => !automationName.trim() || !description.trim(),
    [automationName, description]
  );

  return (
    <CXAutomationAccordion
      number={1}
      title={t({
        id: "cx-actions-page.automation-general",
        defaultMessage: "General",
      })}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <div className="flex flex-col gap-10">
        <div className="flex flex-col gap-4">
          <SectionDescription
            title={t({
              id: "cx-actions-page.automation-name",
              defaultMessage: "Automation name",
            })}
            description={t({
              id: "cx-actions-page.automation-name-description",
              defaultMessage:
                "Set a clear name for this action to help the AI Agent understand when to use it.",
            })}
          />
          <TextInput
            placeholder={t({
              id: "common.enter",
              defaultMessage: "Enter",
            })}
            value={automationName}
            onChange={(e) =>
              onChange({ automationName: e.target.value.trimStart() })
            }
          />
        </div>

        <div className="flex flex-col gap-4">
          <SectionDescription
            title={t({
              id: "cx-actions-page.automation-behavior-title",
              defaultMessage: "When & how should AI Agent act?",
            })}
            description={t({
              id: "cx-actions-page.automation-behavior-description",
              defaultMessage:
                "Define when the action should be triggered and how the AI Agent should handle it in conversations. Add details and example request",
            })}
          />
          <TextAreaInput
            placeholder={t({
              id: "cx-actions-page.automation-behavior-placeholder",
              defaultMessage:
                "Example:\n1. When customer asks where their order is\n2. Send order number to API\n3. Get status from system\n4. Show the result to the customer",
            })}
            value={description}
            onChange={(e) =>
              onChange({ description: e.target.value.trimStart() })
            }
            classNames={{
              inputClassName: "min-h-[300px] text-sm max-h-[300px]",
            }}
          />
        </div>

        <Button
          isDisabled={isSaveDisabled}
          className="w-[200px]"
          iconEnd={<ArrowIcon />}
          onClick={onProceed}
        >
          {t({
            id: "cx-actions-page.save-and-proceed",
            defaultMessage: "Save & Proceed",
          })}
        </Button>
      </div>
    </CXAutomationAccordion>
  );
};
